/**
 * Storey and category visibility.
 *
 * Both are driven through OBC.Classifier so the two toggle sets behave
 * identically. Note the IFC-domain subtlety: what users call "layers" are IFC
 * *classes* (IFCWALL, IFCDOOR...), not IfcPresentationLayerAssignment. We
 * expose classes plus storeys, which is what every BIM tool means by it.
 *
 * API verified against @thatopen/components 3.4.8 type definitions.
 */
import * as OBC from "@thatopen/components";
import { CATEGORY_ORDER } from "../core/config";

/** Classification bucket names we create; also the Classifier's map keys. */
const CLASSIFICATION = {
  STOREYS: "Storeys",
  CATEGORIES: "Categories",
} as const;

export interface VisibilityGroup {
  readonly id: string;
  readonly label: string;
  visible: boolean;
}

export interface VisibilityController {
  readonly storeys: readonly VisibilityGroup[];
  readonly categories: readonly VisibilityGroup[];
  setStoreyVisible(id: string, visible: boolean): Promise<void>;
  setCategoryVisible(id: string, visible: boolean): Promise<void>;
  showAll(): Promise<void>;
}

export async function createVisibilityController(
  components: OBC.Components,
): Promise<VisibilityController> {
  const classifier = components.get(OBC.Classifier);
  const hider = components.get(OBC.Hider);

  await classifier.byIfcBuildingStorey({ classificationName: CLASSIFICATION.STOREYS });
  await classifier.byCategory({ classificationName: CLASSIFICATION.CATEGORIES });

  const storeys = readGroups(classifier, CLASSIFICATION.STOREYS, sortByLabel);
  const categories = readGroups(classifier, CLASSIFICATION.CATEGORIES, sortByCategoryOrder);

  async function apply(
    bucket: string,
    groups: readonly VisibilityGroup[],
    id: string,
    visible: boolean,
  ): Promise<void> {
    const group = groups.find((candidate) => candidate.id === id);
    if (group === undefined) return;
    const groupData = classifier.list.get(bucket)?.get(id);
    if (groupData === undefined) return;
    group.visible = visible;
    await hider.set(visible, await groupData.get());
  }

  return {
    storeys,
    categories,
    setStoreyVisible: (id, visible) => apply(CLASSIFICATION.STOREYS, storeys, id, visible),
    setCategoryVisible: (id, visible) => apply(CLASSIFICATION.CATEGORIES, categories, id, visible),
    async showAll() {
      for (const group of [...storeys, ...categories]) group.visible = true;
      await hider.set(true);
    },
  };
}

function readGroups(
  classifier: OBC.Classifier,
  bucket: string,
  sort: (a: VisibilityGroup, b: VisibilityGroup) => number,
): VisibilityGroup[] {
  const found = classifier.list.get(bucket);
  if (found === undefined) return [];

  const groups: VisibilityGroup[] = [];
  for (const [id] of found) {
    groups.push({ id, label: humanise(id), visible: true });
  }
  return groups.sort(sort);
}

function sortByLabel(a: VisibilityGroup, b: VisibilityGroup): number {
  return a.label.localeCompare(b.label, undefined, { numeric: true });
}

/**
 * Known classes first in the order architects expect; anything else after,
 * alphabetically. This orders — it never filters — so an unusual model keeps
 * every toggle.
 */
function sortByCategoryOrder(a: VisibilityGroup, b: VisibilityGroup): number {
  const rankA = CATEGORY_ORDER.indexOf(a.id.toUpperCase());
  const rankB = CATEGORY_ORDER.indexOf(b.id.toUpperCase());
  if (rankA !== -1 && rankB !== -1) return rankA - rankB;
  if (rankA !== -1) return -1;
  if (rankB !== -1) return 1;
  return sortByLabel(a, b);
}

/** "IFCWALLSTANDARDCASE" -> "Wall Standard Case" */
function humanise(raw: string): string {
  const withoutPrefix = raw.replace(/^IFC/i, "");
  const spaced = withoutPrefix.replace(/([a-z])([A-Z])/g, "$1 $2");
  if (spaced === spaced.toUpperCase()) {
    return spaced.charAt(0) + spaced.slice(1).toLowerCase();
  }
  return spaced;
}
